/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * MoreOverlay — vertical list of overflow sections shown when "More" tab is tapped.
 *
 * Renders inside bn-overlay-container, reuses bn-overlay-in animation.
 * Each row shows section icon + label + badge dot. Tapping a row
 * closes the overlay and expands that section's sub-items normally.
 */

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { BottomNavSection } from './types';

interface Props {
  sections: BottomNavSection[];
  onSelectSection: (sectionId: string) => void;
}

export function MoreOverlay({ sections, onSelectSection }: Props) {
  const { t } = useTranslation('common');
  const location = useLocation();

  return (
    <div className="bn-more-panel">
      <div className="bn-more-title">{t('bottomNav.more', 'More')}</div>
      {sections.map((section) => {
        const Icon = section.icon ?? section.items[0]?.icon;
        const totalBadges = section.items.reduce((sum, item) => sum + (item.badge ?? 0), 0);
        const isActive = section.items.some((item) =>
          location.pathname.startsWith(item.href)
        );

        return (
          <button
            key={section.id}
            className={`bn-more-row${isActive ? ' bn-more-active' : ''}`}
            onClick={() => onSelectSection(section.id)}
            type="button"
          >
            <div className="bn-more-icon">
              {Icon && <Icon size={20} />}
            </div>
            <span className="bn-more-label">{t(section.titleKey)}</span>
            {totalBadges > 0 && <span className="bn-more-badge-dot" />}
          </button>
        );
      })}
    </div>
  );
}
